import Container from '@/components/Container';

import KnowledgeBaseSkeleton from './KnowledgeBase.skeleton';
import useIsTablet from '@/hooks/useIsTablet';
import { useQueryCacheAndNetwork } from '@/hooks/useQueryCacheAndNetwork';
import {
  QUERY_FETCH_KNOWLEDGE_BASE_PAGE,
  KnowledgeBasePageQuery,
  KnowledgeBasePageQueryVariables,
} from '@edwin-group/people-graphql';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import Button from '@/components/Button';
import KnowledgeBaseTypesFilter from './ResourceTypesFilter';
import { useKnowledgeBaseList } from './hooks/useKnowledgeBaseList';
import { useKnowledgeBaseListStore } from './store/useKnowledgeBaseListStore';
import type { KnowledgeBase } from '@edwin-group/people-graphql';
import PageHeader from '@/components/PageHeader';
import KnowledgeBaseCard from '@/components/KnowledgeBaseCard';

function renderCards({
  data,
  viewAll = false,
  viewAllText = 'View all',
  handleLoadMore,
}: {
  data: KnowledgeBase[];
  viewAll?: boolean;
  viewAllText?: string;
  handleLoadMore?: () => void;
}) {
  if (!data?.length) {
    return null;
  }

  return (
    <AnimatedFadeInComponent>
      <div className="mb-sh-30">
        <div className="flex flex-col gap-sh-30">
          {data?.map((knowledgebase: KnowledgeBase) => {
            return (
              <KnowledgeBaseCard
                key={knowledgebase?.slug}
                title={knowledgebase.title || ''}
                description={knowledgebase.briefDescription || ''}
                thumbnail={knowledgebase.cover || undefined}
                url={`/knowledge-base/${knowledgebase.slug}`}
                duration={knowledgebase.duration || undefined}
                tag={knowledgebase.knowledgeBaseType?.type?.toString() || undefined}
              />
            );
          })}
        </div>
        {viewAll && handleLoadMore && (
          <div className="mt-sh-30 flex w-full">
            <Button theme="solid" onClick={handleLoadMore}>
              {viewAllText}
            </Button>
          </div>
        )}
      </div>
    </AnimatedFadeInComponent>
  );
}

export default function KnowledgeBase() {
  const { data: knowledgeBasePage, loading: loadingPage } = useQueryCacheAndNetwork<
    KnowledgeBasePageQuery,
    KnowledgeBasePageQueryVariables
  >(QUERY_FETCH_KNOWLEDGE_BASE_PAGE);

  const { loadMore, hasMore, shouldFetch } = useKnowledgeBaseList();
  const knowledgeBases = useKnowledgeBaseListStore((state) => state.knowledgeBases);
  const rootCategories = useKnowledgeBaseListStore((state) => state.rootCategories);
  const selectedCategoryIds = useKnowledgeBaseListStore((state) => state.selectedCategoryIds);
  const selectedMainCategoryName = rootCategories.find(
    (category) => category.documentId === selectedCategoryIds[0],
  )?.category;

  const { isTablet } = useIsTablet();
  if (loadingPage) {
    return <KnowledgeBaseSkeleton />;
  }

  return (
    <>
      <PageHeader
        title={knowledgeBasePage?.knowledgeBasePage?.title || 'Knowledge Base'}
        description={knowledgeBasePage?.knowledgeBasePage?.description || ''}
      />
      <Container className="!mt-sh-30 mt-sh-60 min-h-40" addMargin={!isTablet}>
        <div className="col-span-12">
          <KnowledgeBaseTypesFilter />
          {selectedMainCategoryName ? (
            <h2 className="my-[20px] font-haffer text-sh-24 font-bold text-black-200">{selectedMainCategoryName}</h2>
          ) : null}

          {shouldFetch &&
            renderCards({
              data: knowledgeBases as KnowledgeBase[],
              viewAll: hasMore,
              viewAllText: knowledgeBasePage?.knowledgeBasePage?.moreButtonTitle || 'View all',
              handleLoadMore: loadMore,
            })}
        </div>
      </Container>
    </>
  );
}
